import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';

import { ShapeOption } from '@vestico/model';

import RadioButton from './RadioButton';
import { SVGImage } from './SVGImage';
import style from './shapeselect.module.css';

export interface InputProps {
  shapeOptions: ShapeOption[];
  selectedOptionIndex: number;
}

interface OwnProps {
  inputProps: InputProps;
  title: string;
  error?: string;
  onUpdate: (selectedShapeOption: ShapeOption) => void;
}

export const ShapeSelect = ({ title, error, inputProps, onUpdate }: OwnProps) => {
  const [imageName, setImageName] = useState<undefined | string>(undefined);
  const { selectedOptionIndex, shapeOptions } = inputProps;

  const updateSelectedShapeImage = useCallback(() => {
    const selectedIndex = selectedOptionIndex;
    const selectedShapeOption = shapeOptions[selectedIndex];

    if (!selectedShapeOption) {
      return;
    }
    setImageName(selectedShapeOption.image);
  }, [selectedOptionIndex, shapeOptions]);

  const onRadioButtonChanged = useCallback(
    (optionId: string) => {
      let selectedIndex;
      for (const shapeOption of shapeOptions) {
        if (shapeOption.shape.toString() === optionId) {
          selectedIndex = shapeOption.shape;
          break;
        }
      }
      if (selectedIndex === undefined) {
        console.error(`unknown id ${optionId}`);
        return;
      }

      if (selectedIndex >= shapeOptions.length) {
        console.error('invalid index selected');
        return;
      }

      inputProps.selectedOptionIndex = selectedIndex;
      updateSelectedShapeImage();
      onUpdate(shapeOptions[selectedIndex]);
    },
    [inputProps, onUpdate, shapeOptions, updateSelectedShapeImage]
  );

  // Call first to set image paths
  useEffect(() => {
    // set our options
    updateSelectedShapeImage();
    // preload images
    shapeOptions.forEach((shapeOption) => {
      import(`sharedimages/${shapeOption.image}.svg`);
    });
  }, [updateSelectedShapeImage, shapeOptions]);

  const image = imageName ? <SVGImage name={imageName} showSpinner /> : null;

  return (
    <>
      <div className={clsx(style.root)}>
        <div className={clsx(style.container)}>{image}</div>
        <fieldset id={title} className={clsx(style.buttonsRow)}>
          <legend className={clsx(style.legend)}>{title}</legend>
          {shapeOptions.map((shapeOption, index) => {
            const id = shapeOption.shape.toString();
            return (
              <RadioButton
                groupId={title}
                key={id}
                id={id}
                displayValue={shapeOption.name}
                checked={inputProps.selectedOptionIndex === index}
                didTrigger={onRadioButtonChanged}
              />
            );
          })}
        </fieldset>
      </div>
      {error && <hr />}
      {error && <div className={clsx(style.error)}>{error}</div>}
    </>
  );
};
